import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { selectSelectedAccountId } from '@mkp/account/state';
import { authWorkspaceGuardActions } from '@mkp/auth/actions';
import { selectWorkspaceFromUrlStatus, WorkspaceFromUrlStatus } from '@mkp/auth/state';
import { isUuid } from '@mkp/shared/models';
import { AppConfig, LocalStorageKeys } from '@mkp/shared/util-app-config';
import { VacancyResource } from '@mkp/vacancy/data-access';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

/**
 * Redirects workspace-agnostic vacancy URLs (e.g. from marketing campaigns) to their
 * workspace-prefixed equivalents, preserving all query params (UTMs, vouchers, etc.).
 *
 * Three cases:
 * 1. Workspace already selected → prefix the URL immediately (fast-path).
 * 2. URL contains a vacancy UUID → look up its accountId via API, then prefix.
 * 3. No workspace, no UUID (e.g. /vacancy/new) → save the URL to
 *    localStorage so SelectWorkspaceComponent can resume after workspace selection.
 *
 * The parent `activeUserGuard` ensures `workspaceFromUrlStatus` has left INIT
 * before this guard runs, so the filter passes on the first emission.
 */
export const redirectVacancyToWorkspaceGuard = (): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const store = inject(Store);
  const vacancyResource = inject(VacancyResource);
  const url = router.currentNavigation()?.extractedUrl;

  return store.select(selectWorkspaceFromUrlStatus).pipe(
    filter((status) => status !== WorkspaceFromUrlStatus.INIT),
    take(1),
    concatLatestFrom(() => store.select(selectSelectedAccountId)),
    switchMap(([, selectedAccountId]) => {
      // Case 1: workspace known (single-workspace auto-selected, or already chosen)
      if (selectedAccountId && url) {
        return of(buildWorkspaceUrl(router, selectedAccountId, url));
      }

      // Case 2: URL contains a valid vacancy UUID — resolve its workspace via API
      const vacancyId = extractVacancyId(url);
      if (vacancyId) {
        return vacancyResource.getById(vacancyId).pipe(
          map(({ accountId }) => buildWorkspaceUrl(router, accountId, url)),
          catchError(() => of(router.parseUrl(AppConfig.routes.error404)))
        );
      }

      // Case 3: multi-workspace user with no vacancy UUID (e.g. /vacancy/new?utm_source=...)
      // Save the full URL so SelectWorkspaceComponent can navigate here after selection.
      // A dedicated key avoids collisions with `redirectUrlGuard` / auth flow.
      localStorage.setItem(
        LocalStorageKeys.workspaceSelectionRedirectUri,
        router.serializeUrl(url!)
      );
      // Hands off to workspace selection the same way hasSelectedWorkspaceGuard does. The action
      // is what takes the splash screen down and settles workspaceFromUrlStatus; without it this
      // route was the only way into /select-workspace that left the splash up.
      store.dispatch(authWorkspaceGuardActions.redirectedOnNoSelectedWorkspace());
      return of(router.parseUrl(AppConfig.routes.selectAccountMembership));
    })
  );
};

// Legacy url shapes where the vacancy id follows a fixed prefix segment (pre-hub, e.g.
// /vacancy/publish/{uuid}). Current urls carry the id at segment[1] and hit the fallback below.
const PATH_PREFIXES_WITH_VACANCY_ID = new Set([
  AppConfig.routes.publish,
  AppConfig.routes.edit,
  AppConfig.routes.preview,
]);

/**
 * Extracts a vacancy UUID from either the `?search=` query param or a URL segment.
 * Non-UUID values (e.g. "new") are rejected to avoid invalid API calls.
 *
 * Supported URL shapes:
 *   /vacancy/{uuid}[/edit|/publish/…]      → uuid at segment[1] (current :id-hub urls)
 *   /vacancy/{publish|edit|preview}/{uuid} → uuid at segment[2] (legacy, still redirected)
 *   /vacancies?search={uuid}                → uuid from query param (also the legacy /vacancy path)
 */
const extractVacancyId = (url: UrlTree | undefined): string | undefined => {
  const segments = getPrimarySegments(url);
  const [first, second, third] = segments;

  // ?search={uuid} — only when the path is exactly the list, to avoid future collisions with
  // other routes that might use ?search=uuid (e.g. /user?search=uuid). Both the current
  // /vacancies list and the legacy /vacancy path are mounted with this guard.
  const isListPath = first === AppConfig.routes.vacancies || first === AppConfig.routes.vacancy;
  if (segments.length === 1 && isListPath) {
    const search = url?.queryParams?.['search'];
    if (search && isUuid(search)) return search;
  }

  const vacancyId = PATH_PREFIXES_WITH_VACANCY_ID.has(second) ? third : second;
  return vacancyId && isUuid(vacancyId) ? vacancyId : undefined;
};

const buildWorkspaceUrl = (router: Router, accountId: string, url: UrlTree | undefined): UrlTree =>
  router.createUrlTree(['/', AppConfig.routes.workspace, accountId, ...getPrimarySegments(url)], {
    queryParams: url?.queryParams,
  });

const getPrimarySegments = (url: UrlTree | undefined): string[] =>
  url?.root?.children?.['primary']?.segments?.map((s) => s.path) ?? [];
